import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  AccountStates,
  type AccountType,
  SourceOfFunds,
  SubStepTypes,
} from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { Transfer } from '@interfaces/transfer';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { appendUpdatedAccount } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { applicableProvinces, provinces } from '@ovComponents/resources';
import { UserContext } from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useSnackbar } from 'hephaestus/web';
import { find, isEmpty } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { getEnabledCustomFields } from '../utils';
import { UPDATE_AFFILIATIONS } from './components/affiliateMutations';
import { AFFILIATION_STATE_FIELDS } from './components/affiliationStateFields';
import { mergeAccountAffiliations } from './components/mergeAccountAffiliations';
import { resolveUnsavedAccountFields } from './components/unsavedAccountFields';
import { EditAccountVisual } from './editAccount.visual';

export const FETCH_ACCOUNT_TYPES = gql`
  query fetchAccountTypes {
    fetchAccountTypes {
      accountTypes {
        value
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const ACCOUNT = ({
  canReadCustodianConnection,
}: {
  canReadCustodianConnection?: boolean;
} = {}) => `#graphql
  id: stringId
  type
  state
  applyForGovFunds
  jurisdiction
  sourceOfFunds
  eligibleForDisabilityTaxCredit
  repCode { code }
  groupEmployer
  groupNumber
  rightOfSurvivorship
  subAccounts { id state }
  respAllBeneficiariesAreInBc
  user {
    id: stringId
    firstName
    lastName
    primaryEmail
    taxIdExists
    physicalAddress { country city province streetName postal unitNumber houseNumber neighborhood }
    maritalStatus
    countryOfTaxResidence
    type
    accessibleBy (input: { excludeRepCodeUsers: $excludeRepCodeUsers }) {
      relation
      organizationUser {
        id
        repCodes { code name }
      }
    }
  }
  scheduledIncomeFundTransfer {
    id
    scheduledDate
    minimumAnnualAmountCents
    annualAmountCents
    state
    subAccount {
      id
      theme { id translatedName { ${TRANSLATED_STRING_FIELDS} } }
    }
    bankAccount {
      id
      name
      bankAccountNumber
    }
    frequency
    amountPayableType
    dateOfBirth
    taxOption
    specialTaxRate
    totalWithdrawalAmountOption
  }
  householdClientGroup { id: stringId name }
  affiliations { ${AFFILIATION_STATE_FIELDS} }
  baseCurrency
  availableCurrencies
  custodianAffiliates{
    allocation
    relation
    type
    contributionAmount
    grantAmount
    user {
      dateOfBirth
      lastName
      firstName
    }
  }
  ${
    canReadCustodianConnection
      ? `#graphql
    custodianConnection {
      id
      name
      default
      accountTypeSettings {
        type
        baseCurrency
        availableCurrencies
        isMultiCurrencyEnabled
        affiliateSettings {
          affiliateType
          requiredFields
          optionalFields
          description
        }
      }
   }
  `
      : ''
  }
`;

const FETCH_USER_ACCOUNT = (
  derivedCustomFields?: string[],
  { canReadCustodianConnection }: { canReadCustodianConnection?: boolean } = {},
) => gql`
  query fetchUserAccount($userId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchUser(input: { userId: $userId }) {
      user {
        accounts {
          ${ACCOUNT({ canReadCustodianConnection })}
          ${
            derivedCustomFields
              ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
            key
            value
            customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
            selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
          }`
              : ''
          }
        }
      }
    }
  }
`;

const FETCH_ACCOUNT = (
  derivedCustomFields?: string[],
  { canReadCustodianConnection }: { canReadCustodianConnection?: boolean } = {},
) => gql`
  query fetchAccount($accountId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        ${ACCOUNT({ canReadCustodianConnection })}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const FETCH_CUSTODIAN_CONNECTIONS = gql`
  query fetchCustodianConnections($input: FetchCustodianConnectionsInput!) {
    fetchCustodianConnections(input: $input) {
      custodianConnections {
        id
        name
        default
        accountTypeSettings {
          type
          baseCurrency
          availableCurrencies
          isMultiCurrencyEnabled
          affiliateSettings {
            affiliateType
            requiredFields
            optionalFields
            description
          }
        }
      }
    }
  }
`;

export const useAccountData = () =>
  useState<
    Partial<
      Account & {
        accountId: string | null;
        householdClientGroupId?: string;
        scheduledIncomeFundTransfer: Transfer;
      }
    >
  >({ accountId: null, customFields: [] });

type FetchAccountResult = {
  fetchAccount?: {
    account?: Partial<Account>;
  };
};

export const useFetchAccount = (accountId?: string) => {
  const canReadCustodianConnection = usePermission('read:custodian_connection');
  const { workflowData } = useContext(WorkflowContext);
  const repcodeAccessEnabled = useFeatureFlag(FlagsmithFlags.repcode_access);
  return useLazyQuery<FetchAccountResult>(
    FETCH_ACCOUNT(undefined, { canReadCustodianConnection }),
    {
      variables: {
        accountId: accountId || workflowData?.currentAccountId,
        skipErrorHandler: true,
        excludeRepCodeUsers: !repcodeAccessEnabled,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
  );
};

export const EditAccount = ({
  options,
  userId,
  onNext,
  previousStep,
  stepLoading,
  grid = false,
  updateMode = false,
  accountId,
  direction,
  workflowCompletion,
  isWidget = false,
}: {
  options: any;
  userId?: string;
  onNext: () => void;
  previousStep: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  accountId?: string;
  direction: 'FORWARD' | 'BACKWARD';
  workflowCompletion?: any;
  isWidget?: boolean;
}) => {
  const canReadCustomFields = usePermission('read:custom_fields');
  const canReadAccountTypes = usePermission('read:account_types');
  const canReadCustodianConnection = usePermission('read:custodian_connection');
  const { t } = useTranslation(['client', 'accountsDetail']);
  const { enqueueSnackbar } = useSnackbar();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const {
    activeOrganization,
    custodianConnection: defaultCustodianConnection,
  } = useContext(UserContext);
  const repcodeAccessEnabled = useFeatureFlag(FlagsmithFlags.repcode_access);
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const [custodianConnections, setCustodianConnections] = useState<any[]>();
  const [accountData, setAccountData] = useAccountData();
  const hydratedAccountIdRef = useRef<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const enabledCustomFields = getEnabledCustomFields(options);
  const skipFetchAccount =
    submitting ||
    (((!accountId && !workflowData.currentAccountId) ||
      (canReadCustomFields && enabledCustomFields.length !== 0)) &&
      !workflowData.skipEditAccount);
  const skipFetchUserAccount =
    (!!workflowData.currentAccountId ||
      !!accountId ||
      (canReadCustomFields && enabledCustomFields.length !== 0)) &&
    !workflowData.skipEditAccount;
  const [isCurrencyFieldsDisabled, setIsCurrencyFieldsDisabled] =
    useState<boolean>(false);

  const { data: accountTypesData } = useQuery(FETCH_ACCOUNT_TYPES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id ?? undefined,
        },
      },
      skip:
        !canReadAccountTypes ||
        workflowData.skipEditAccount ||
        !accountData.type,
    },
  });

  const [currentAccountTypeData, setCurrentAccountTypeData] =
    useState<AccountType | null>(null);

  useEffect(() => {
    if (accountTypesData) {
      const currentAccountType = accountData.type;
      const currentAccountTypeData =
        accountTypesData?.fetchAccountTypes?.accountTypes?.find(
          (a: AccountType) => a.value === currentAccountType,
        );
      if (currentAccountTypeData) {
        setCurrentAccountTypeData(currentAccountTypeData);
      }
    }
  }, [accountTypesData, accountData.type]);

  const { loading: customFieldLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.EditAccount,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: { perPage: 100 },
      },
      skipErrorHandler: true,
    },
    skip:
      (!canReadCustomFields || enabledCustomFields.length === 0) &&
      !workflowData.skipEditAccount,
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
    onCompleted: async (res: any) => {
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);

      if (accountId || workflowData.currentAccountId) {
        await fetchAccount({
          query: customFieldKeys
            ? FETCH_ACCOUNT(customFieldKeys, { canReadCustodianConnection })
            : undefined,
        });
      } else {
        await fetchUserAccount({
          variables: {
            userId,
            skipErrorHandler: true,
            excludeRepCodeUsers: !repcodeAccessEnabled,
          },
          query: customFieldKeys
            ? FETCH_USER_ACCOUNT(customFieldKeys, {
                canReadCustodianConnection,
              })
            : undefined,
        });
      }
    },
  });

  const { loading: custodianConnectionsLoading } = useQuery(
    FETCH_CUSTODIAN_CONNECTIONS,
    {
      variables: {
        input: {
          filter: { organizationId: activeOrganization?.id ?? undefined },
          pagination: { perPage: 100 },
        },
        skipErrorHandler: true,
      },
      skip:
        (!options.custodianSelector || !canReadCustodianConnection) &&
        !workflowData.skipEditAccount,
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
      onCompleted: async (res: any) => {
        setCustodianConnections(
          res?.fetchCustodianConnections?.custodianConnections,
        );
      },
    },
  );

  const [fetchAccount, { data, refetch: accountRefetch, loading }] =
    useFetchAccount(accountId);

  const setAccountDataFunc = (response: any): void => {
    setAccountData((state: typeof accountData) => {
      const uniqueCustomFieldsFromResponse =
        response.fetchAccount.account.customFields?.filter(
          (cf: { key: string }) =>
            !state.customFields?.some(
              (_cf: { key: string }) => _cf.key === cf.key,
            ),
        ) || [];
      const accountState =
        response.fetchAccount.account?.state ?? AccountStates.Initiated;
      const incomingAccountId = response.fetchAccount.account.id;
      const alreadyHydrated =
        !!state.accountId && state.accountId === incomingAccountId;
      const unsavedAccountFields = resolveUnsavedAccountFields({
        server: {
          applyForGovFunds: response.fetchAccount.account.applyForGovFunds,
          eligibleForDisabilityTaxCredit:
            response.fetchAccount.account.eligibleForDisabilityTaxCredit ??
            undefined,
          respAllBeneficiariesAreInBc:
            response.fetchAccount.account.respAllBeneficiariesAreInBc,
        },
        local: {
          applyForGovFunds: state.applyForGovFunds,
          eligibleForDisabilityTaxCredit: state.eligibleForDisabilityTaxCredit,
          respAllBeneficiariesAreInBc: state.respAllBeneficiariesAreInBc,
        },
        alreadyHydrated,
      });

      if (
        ![AccountStates.Initiated, AccountStates.Ready].includes(accountState)
      ) {
        setIsCurrencyFieldsDisabled(true);
      } else {
        setIsCurrencyFieldsDisabled(false);
      }

      hydratedAccountIdRef.current = incomingAccountId;

      return {
        ...response.fetchAccount.account,
        state: accountState,
        accountId: incomingAccountId,
        affiliations: mergeAccountAffiliations(
          response.fetchAccount.account.affiliations,
          state.affiliations,
        ),
        custodianAffiliates:
          response.fetchAccount.account.custodianAffiliates || [],
        sourceOfFunds: response.fetchAccount.account.sourceOfFunds ?? undefined,
        eligibleForDisabilityTaxCredit:
          unsavedAccountFields.eligibleForDisabilityTaxCredit,
        jurisdiction: response.fetchAccount.account.jurisdiction ?? undefined,
        householdClientGroupId:
          response.fetchAccount.account.householdClientGroup?.id ?? undefined,
        customFields: [
          ...uniqueCustomFieldsFromResponse,
          ...(state.customFields || []),
        ],
        baseCurrency:
          response.fetchAccount.account.baseCurrency ?? CurrencyCodes.CAD,
        availableCurrencies:
          response.fetchAccount.account.availableCurrencies &&
          response.fetchAccount.account.availableCurrencies.length > 0
            ? response.fetchAccount.account.availableCurrencies
            : [CurrencyCodes.CAD],
        custodianConnection:
          response.fetchAccount.account.custodianConnection ??
          defaultCustodianConnection,
        respAllBeneficiariesAreInBc:
          unsavedAccountFields.respAllBeneficiariesAreInBc,
        applyForGovFunds: unsavedAccountFields.applyForGovFunds,
      };
    });
  };

  const {
    data: defaultdata,
    refetch,
    loading: fetchAccountLoading,
  } = useQuery(FETCH_ACCOUNT(undefined, { canReadCustodianConnection }), {
    variables: {
      accountId: accountId || workflowData?.currentAccountId,
      skipErrorHandler: true,
    },
    fetchPolicy: 'network-only',
    skip: skipFetchAccount,
    errorPolicy: 'ignore',
  });

  const [
    fetchUserAccount,
    { data: fetchedUserAccountData, refetch: refetchUserAccount },
  ] = useLazyQuery(
    FETCH_USER_ACCOUNT(undefined, { canReadCustodianConnection }),
    {
      variables: {
        userId,
        skipErrorHandler: true,
        excludeRepCodeUsers: !repcodeAccessEnabled,
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
  );

  const {
    data: userData,
    refetch: userRefetch,
    loading: userLoading,
  } = useQuery(FETCH_USER_ACCOUNT(undefined, { canReadCustodianConnection }), {
    variables: {
      userId,
      skipErrorHandler: true,
      excludeRepCodeUsers: !repcodeAccessEnabled,
    },
    fetchPolicy: 'network-only',
    skip: skipFetchUserAccount,
    errorPolicy: 'ignore',
  });

  useEffect(() => {
    if (derivedCustomFields) {
      const fields = derivedCustomFields.map((a) => a.key);
      setDerivedCustomFieldsKeys(fields);
    }
  }, [derivedCustomFields]);

  const [updateAccount, { loading: updateAccountLoading }] = useMutation(
    UPDATE_ACCOUNT,
    {
      variables: {
        input: {
          accountId: accountData.accountId,
          applyForGovFunds: accountData.applyForGovFunds,
          jurisdiction: accountData.jurisdiction ?? undefined,
          sourceOfFunds: accountData.sourceOfFunds ?? undefined,
          groupEmployer: accountData.groupEmployer ?? undefined,
          groupNumber: accountData.groupNumber ?? undefined,
          eligibleForDisabilityTaxCredit:
            accountData.eligibleForDisabilityTaxCredit ?? undefined,
          householdClientGroupId:
            accountData.householdClientGroupId ?? undefined,
          customFields: modifyUpdatingCustomFields(
            accountData?.customFields || [],
            derivedCustomFields || [],
            derivedCustomFieldsKeys,
          ),
          ...(isCurrencyFieldsDisabled
            ? {}
            : { baseCurrency: accountData.baseCurrency }),
          ...(isCurrencyFieldsDisabled
            ? {}
            : { availableCurrencies: accountData.availableCurrencies }),
          custodianConnectionId:
            accountData.custodianConnection?.id ?? undefined,
          repCode: accountData.repCode?.code,
          rightOfSurvivorship: accountData.rightOfSurvivorship ?? undefined,
          respAllBeneficiariesAreInBc:
            accountData.respAllBeneficiariesAreInBc ?? undefined,
        },
      },
    },
  );

  const [updateAffiliationsMutation] = useMutation(UPDATE_AFFILIATIONS, {
    variables: {
      input: {
        accountId: accountData.accountId,
        affiliations:
          accountData.affiliations?.map((item) => ({
            id: item.id,
            allocation: item.allocation,
            relation: item.relation,
            type: item.type,
            userId: item.user.id,
            relatedAffiliations: item.relatedAffiliations,
            invitationStatus: item?.invitationStatus,
            roleId: item?.roleId,
          })) || [],
      },
    },
  });
  const updateAffiliations = async () => {
    await updateAffiliationsMutation();
    await setWorkflowData((prev) => ({
      ...prev,
      currentAccountId: accountData.accountId || undefined,
      currentAccountType: accountData.type,
    }));
    await updateAccount();
    if (!isWidget) {
      await setWorkflowData((prev) =>
        appendUpdatedAccount(prev, {
          id: accountData.accountId || undefined,
          type: accountData.type,
          state: accountData.state,
        }),
      );
    }
    onNext();
    if (updateMode) {
      enqueueSnackbar(t('shared:profileDetailsUpdated'), {
        variant: 'success',
      });
    }
  };

  useEffect(() => {
    const incoming = data ?? defaultdata;
    const incomingAccountId = incoming?.fetchAccount?.account?.id;
    if (!incomingAccountId) {
      return;
    }
    if (hydratedAccountIdRef.current === incomingAccountId) {
      return;
    }
    setAccountDataFunc(incoming);
  }, [data, defaultdata]);

  const setUserAccountDataFunc = (response: any): void => {
    const firstAccount = response.fetchUser.user.accounts[0];
    if (!firstAccount) {
      return;
    }
    hydratedAccountIdRef.current = firstAccount.id;
    setAccountData({
      state: firstAccount?.state ?? AccountStates.Initiated,
      accountId: firstAccount.id,
      type: firstAccount.type || undefined,
      repCode: firstAccount.repCode,
      groupEmployer: firstAccount.groupEmployer ?? undefined,
      groupNumber: firstAccount.groupNumber ?? undefined,
      applyForGovFunds: firstAccount.applyForGovFunds || [],
      user: firstAccount.user,
      affiliations: firstAccount.affiliations || [],
      custodianAffiliates: firstAccount.custodianAffiliates || [],
      subAccounts: firstAccount.subAccounts,
      scheduledIncomeFundTransfer: firstAccount.scheduledIncomeFundTransfer,
      sourceOfFunds: firstAccount.sourceOfFunds ?? undefined,
      jurisdiction: firstAccount.jurisdiction ?? undefined,
      householdClientGroupId: firstAccount.householdClientGroupId ?? undefined,
      customFields: firstAccount?.customFields || [],
      baseCurrency: firstAccount?.baseCurrency ?? CurrencyCodes.CAD,
      availableCurrencies:
        firstAccount?.availableCurrencies &&
        firstAccount?.availableCurrencies.length > 0
          ? firstAccount?.availableCurrencies
          : [CurrencyCodes.CAD],
      custodianConnection:
        firstAccount?.custodianConnection ?? defaultCustodianConnection,
    });
  };

  useEffect(() => {
    const incoming = userData ?? fetchedUserAccountData;
    const incomingAccountId = incoming?.fetchUser?.user?.accounts?.[0]?.id;
    if (!incomingAccountId) {
      return;
    }
    if (hydratedAccountIdRef.current === incomingAccountId) {
      return;
    }
    setUserAccountDataFunc(incoming);
  }, [
    userData,
    fetchedUserAccountData,
    defaultCustodianConnection,
    setAccountData,
  ]);

  const filterApplicableProvinces = (province: {
    key: string;
    value: string;
  }) => {
    const currentAccountType = accountData?.type;
    const availabileRestriction: any = find(applicableProvinces, {
      accountType: currentAccountType,
    });
    if (availabileRestriction) {
      return availabileRestriction.availability.includes(province.key);
    }
    return true;
  };

  function getJurisdictionOptions(): Array<{ id?: string; label?: string }> {
    return provinces
      .filter(filterApplicableProvinces)
      .map((a: any) => ({ id: a.key, label: a.value }));
  }

  function getSourceOfFundsOptions(): Array<{
    id?: string;
    label?: string;
    key?: string;
  }> {
    const currentAccountType = accountData?.type;
    const types = currentAccountType?.startsWith('USA_')
      ? [
          SourceOfFunds.UsaEmploymentWages,
          SourceOfFunds.UsaInheritanceTrust,
          SourceOfFunds.UsaLotteryGaming,
          SourceOfFunds.UsaRetirementFunds,
          SourceOfFunds.UsaInvestments,
          SourceOfFunds.UsaSpousalParentalSupport,
          SourceOfFunds.UsaGift,
          SourceOfFunds.UsaSavings,
          SourceOfFunds.UsaUnemploymentDisability,
          SourceOfFunds.UsaLegalSettlement,
          SourceOfFunds.UsaOther,
        ]
      : [SourceOfFunds.Self, SourceOfFunds.Spouse];
    return types.map((type) => ({
      id: type,
      label: t(
        `workflowCompletions:updateAccount.sourceOfFundsOptions.${type}`,
      ),
    }));
  }

  const getCurrentJurisdictionOption = (key: string) => {
    if (key === '') return undefined;
    const current = provinces.filter((k: any) => k.key === key);
    if (current && current[0]) {
      return current[0].key;
    }
    return undefined;
  };

  const handleSkip = async () => {
    const accountIdToSet =
      accountData.accountId || accountId || workflowData.currentAccountId;
    const accountTypeToSet =
      accountData.type || workflowData.currentAccountType;

    if (accountIdToSet || accountTypeToSet) {
      await setWorkflowData((prev) => ({
        ...prev,
        ...(accountIdToSet && { currentAccountId: accountIdToSet }),
        ...(accountTypeToSet && { currentAccountType: accountTypeToSet }),
      }));
    }
    await onNext();
  };

  return (
    <EditAccountVisual
      userId={userId}
      options={options}
      accountData={accountData}
      updateAccount={setAccountData}
      continueFunc={async () => {
        setSubmitting(true);
        await updateAffiliations();
        setSubmitting(false);
      }}
      loadingData={
        customFieldLoading ||
        loading ||
        fetchAccountLoading ||
        userLoading ||
        custodianConnectionsLoading
      }
      loading={
        customFieldLoading ||
        loading ||
        fetchAccountLoading ||
        userLoading ||
        custodianConnectionsLoading ||
        updateAccountLoading ||
        stepLoading
      }
      refetch={async () => {
        if (workflowData.currentAccountId || accountId) {
          if (skipFetchAccount) {
            const result = await accountRefetch();
            if (result?.data) setAccountDataFunc(result.data);
          } else {
            const result = await refetch();
            if (result?.data) setAccountDataFunc(result.data);
          }
        } else if (skipFetchUserAccount) {
          const result = await refetchUserAccount();
          if (result?.data) setUserAccountDataFunc(result.data);
        } else {
          const result = await userRefetch();
          if (result?.data) setUserAccountDataFunc(result.data);
        }
      }}
      grid={grid}
      updateMode={updateMode}
      skip={direction === 'FORWARD' ? handleSkip : previousStep}
      filterApplicableProvinces={filterApplicableProvinces}
      getJurisdictionOptions={getJurisdictionOptions}
      getSourceOfFundsOptions={getSourceOfFundsOptions}
      getCurrentJurisdictionOption={getCurrentJurisdictionOption}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      custodianConnections={custodianConnections}
      workflowCompletion={workflowCompletion}
      skipEditAccount={workflowData.skipEditAccount ?? false}
      isWidget={isWidget}
      isCurrencyFieldsDisabled={isCurrencyFieldsDisabled}
      currentAccountTypeData={currentAccountTypeData}
    />
  );
};

export default EditAccount;
